import {
  addMonths,
  eachDayOfInterval,
  format,
  getISOWeek,
  getISOWeekYear,
  isSameDay,
  isWeekend,
  startOfMonth,
  subDays,
} from "date-fns";

import type {
  PeopleAvailability,
  PeopleAvailabilityRow,
  PeopleAvailabilitySegment,
  PersonScheduleEvent,
} from "@/api/people";
import type { UnitCapacityTimelineDay } from "@/api/units";
import type { PersonEventColorTone } from "@/lib/personEventColorTones";

export type HorizonMonths = 1 | 3 | 6 | 12;
export type AvailabilityTone = PersonEventColorTone;

export interface CalendarDay {
  date: Date;
  key: string;
  dayIndex: number;
  dayOfMonth: number;
  isWeekend: boolean;
  isToday: boolean;
}

export interface CalendarSpan {
  key: string;
  startDayIndex: number;
  daySpan: number;
  anchorDate: Date;
}

export interface CalendarRange {
  horizon: HorizonMonths;
  start: Date;
  endExclusive: Date;
  days: CalendarDay[];
  monthSpans: CalendarSpan[];
  weekSpans: CalendarSpan[];
}

export interface AvailabilityInterval {
  id?: number;
  eventTypeId?: number;
  eventKey: string;
  eventName: string;
  eventAbbreviation?: string | null;
  eventGroup: "absence" | "overtime";
  isAllDay?: boolean;
  tone: AvailabilityTone;
  fromKey: string;
  toKey: string;
  durationHours: number;
  reason: string;
  projectId?: number | null;
  payable?: boolean;
  canEdit?: boolean;
}

export interface AvailabilityRow {
  id: number;
  name: string;
  fullName: string;
  personnelNumber?: string | null;
  unitName?: string | null;
  isActive?: boolean;
  canEditSchedule?: boolean;
  intervals: AvailabilityInterval[];
}

export interface CapacityDay extends CalendarDay {
  dayKind?: PeopleAvailability["capacity"][number]["day_kind"];
  nominalHours: number;
  capacityHours: number;
  displayAvailablePersonDays: number;
  displayTotalPersonDays: number;
  ratio: number | null;
  visualRatio: number;
  isCritical: boolean;
}

export interface CapacityTeamSummary {
  memberCount: number;
  minimumAvailableCount?: number;
}

function buildSpans(days: CalendarDay[], keyOf: (day: CalendarDay) => string): CalendarSpan[] {
  const spans: CalendarSpan[] = [];
  for (const day of days) {
    const key = keyOf(day);
    const current = spans.at(-1);
    if (current?.key === key) current.daySpan += 1;
    else spans.push({ key, startDayIndex: day.dayIndex, daySpan: 1, anchorDate: day.date });
  }
  return spans;
}

export function buildCalendarRange(
  selectedDate: Date,
  horizon: HorizonMonths,
  today = new Date(),
): CalendarRange {
  const start = startOfMonth(selectedDate);
  const endExclusive = addMonths(start, horizon);
  const days = eachDayOfInterval({ start, end: subDays(endExclusive, 1) }).map(
    (date, dayIndex): CalendarDay => ({
      date,
      key: format(date, "yyyy-MM-dd"),
      dayIndex,
      dayOfMonth: date.getDate(),
      isWeekend: isWeekend(date),
      isToday: isSameDay(date, today),
    }),
  );
  return {
    horizon,
    start,
    endExclusive,
    days,
    monthSpans: buildSpans(days, (day) => format(day.date, "yyyy-MM")),
    weekSpans: buildSpans(
      days,
      (day) => `${getISOWeekYear(day.date)}-W${String(getISOWeek(day.date)).padStart(2, "0")}`,
    ),
  };
}

export function shiftStartMonth(start: Date, delta: -1 | 1): Date {
  return addMonths(startOfMonth(start), delta);
}

export function resetStartMonth(today = new Date()): Date {
  return startOfMonth(today);
}

function adaptAvailabilitySegment(segment: PeopleAvailabilitySegment): AvailabilityInterval | null {
  if (segment.date_to < segment.date_from) return null;
  const seedKey = segment.event_type.seed_key;
  return {
    id: segment.id,
    eventTypeId: segment.event_type.id,
    eventKey: seedKey ?? String(segment.event_type.id),
    eventName: segment.event_type.name,
    eventAbbreviation: segment.event_type.abbreviation,
    eventGroup: segment.event_type.event_group,
    isAllDay: segment.is_all_day,
    tone: segment.event_type.color_tone,
    fromKey: segment.date_from,
    toKey: segment.date_to,
    durationHours: (segment.duration_half_hours ?? 0) / 2,
    reason: segment.reason,
    projectId: segment.project_id,
    payable: segment.payable,
    canEdit: segment.can_edit,
  };
}

export function adaptPersonScheduleEvents(
  userId: number,
  name: string,
  events: PersonScheduleEvent[],
): AvailabilityRow[] {
  return [
    {
      id: userId,
      name,
      fullName: name,
      intervals: events.flatMap((event) => {
        const segment = adaptAvailabilitySegment({ ...event, user_id: userId, can_edit: true });
        return segment === null ? [] : [segment];
      }),
    },
  ];
}

export function adaptPeopleAvailabilityRows(data: PeopleAvailability): AvailabilityRow[] {
  const segmentsByUser = new Map<number, AvailabilityInterval[]>();
  for (const segment of data.segments) {
    const adapted = adaptAvailabilitySegment(segment);
    if (adapted === null) continue;
    const current = segmentsByUser.get(segment.user_id) ?? [];
    current.push(adapted);
    segmentsByUser.set(segment.user_id, current);
  }
  return data.rows.map((row: PeopleAvailabilityRow) => ({
    id: row.user_id,
    name: row.display_name,
    fullName: row.full_name,
    personnelNumber: row.personnel_number,
    unitName: row.unit_name,
    isActive: row.is_active,
    canEditSchedule: row.can_edit_schedule,
    intervals: segmentsByUser.get(row.user_id) ?? [],
  }));
}

export function filterAvailabilityRows(
  rows: AvailabilityRow[],
  query: string | undefined,
  eventTypeIds: ReadonlySet<number> | undefined,
): AvailabilityRow[] {
  const needle = query?.trim().toLocaleLowerCase() ?? "";
  return rows
    .filter(
      (row) =>
        needle === "" ||
        row.name.toLocaleLowerCase().includes(needle) ||
        (row.personnelNumber?.includes(needle) ?? false),
    )
    .map((row) => ({
      ...row,
      intervals:
        eventTypeIds === undefined
          ? row.intervals
          : row.intervals.filter(
              (interval) => interval.eventTypeId != null && eventTypeIds.has(interval.eventTypeId),
            ),
    }));
}

export function adaptPeopleCapacity(
  rangeDays: CalendarDay[],
  data: PeopleAvailability,
  criticalThresholdPct: number | null = null,
): CapacityDay[] {
  const byDate = new Map(data.capacity.map((day) => [day.date, day]));
  const memberCount = data.capacity_meta.active_member_count;
  return rangeDays.map((day) => {
    const source = byDate.get(day.key);
    const nominalHours = safeHours((source?.nominal_half_hours ?? 0) / 2);
    const capacityHours = safeHours((source?.capacity_half_hours ?? 0) / 2);
    const ratio = nominalHours === 0 ? null : capacityHours / nominalHours;
    return {
      ...day,
      dayKind: source?.day_kind,
      isWeekend: source === undefined ? day.isWeekend : source.day_kind === "day_off",
      nominalHours,
      capacityHours,
      displayAvailablePersonDays: ratio === null ? 0 : roundToOneDecimal(ratio * memberCount),
      displayTotalPersonDays: memberCount,
      ratio,
      visualRatio: ratio === null ? 0 : Math.min(ratio, 1),
      isCritical:
        ratio !== null && criticalThresholdPct !== null && ratio < criticalThresholdPct / 100,
    };
  });
}

export function summarizeCapacityTeam(
  rows: AvailabilityRow[],
  warningThresholdPct: number,
): CapacityTeamSummary {
  const memberCount = new Set(rows.map((row) => row.id)).size;
  return {
    memberCount,
    minimumAvailableCount: Math.ceil((memberCount * warningThresholdPct) / 100),
  };
}

function safeHours(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function roundToOneDecimal(value: number): number {
  return Math.round((value + Number.EPSILON) * 10) / 10;
}

export function adaptUnitCalendarDays(
  days: CalendarDay[],
  timeline: UnitCapacityTimelineDay[],
): CalendarDay[] {
  const byDate = new Map(timeline.map((day) => [day.date, day]));
  return days.map((day) => {
    const source = byDate.get(day.key);
    return source === undefined ? day : { ...day, isWeekend: source.shift_hours === 0 };
  });
}

export function adaptCapacityTimeline(
  days: CalendarDay[],
  timeline: UnitCapacityTimelineDay[],
  criticalThresholdRatio: number,
  memberCount: number,
): CapacityDay[] {
  const byDate = new Map(timeline.map((day) => [day.date, day]));
  return days.map((day) => {
    const source = byDate.get(day.key);
    const nominalHours = safeHours(source?.nominal_hours ?? 0);
    const capacityHours = safeHours(source?.capacity_hours ?? 0);
    const ratio = nominalHours === 0 ? null : capacityHours / nominalHours;
    return {
      ...day,
      nominalHours,
      capacityHours,
      displayAvailablePersonDays: ratio === null ? 0 : roundToOneDecimal(ratio * memberCount),
      displayTotalPersonDays: memberCount,
      ratio,
      visualRatio: ratio === null ? 0 : Math.min(ratio, 1),
      isCritical: ratio !== null && ratio < criticalThresholdRatio,
    };
  });
}
